import { loginSchema } from '@edms/contracts';
import { AlertCircle, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { type FormEvent, useState } from 'react';

import { Button, Checkbox, Field, Input } from '@/components/ui/primitives';
import { describeAuthError, useAuth } from '@/features/auth/auth-context';

/**
 * Écran de connexion.
 *
 * Volontairement dépouillé : c'est le premier contact avec le produit, il doit
 * inspirer confiance (§19) sans rien promettre de plus qu'un accès. Aucun
 * indice n'est donné sur l'existence d'un compte — le serveur ne distingue pas
 * les deux cas, l'interface non plus.
 */
export function LoginPage(): React.JSX.Element {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    const parsed = loginSchema.safeParse({ email, password, rememberMe });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Saisie invalide.');
      return;
    }

    setSubmitting(true);
    try {
      await login(parsed.data);
    } catch (cause) {
      setError(describeAuthError(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          {/* Le bloc de marque porte déjà le nom et la baseline : les répéter en
              texte ferait doublon. Le titre reste présent pour les lecteurs
              d'écran via l'attribut alt. */}
          <img
            src="/brand/edms-lockup.png"
            alt="EDMS — Electronic Document Management System"
            width={272}
            height={128}
            className="h-28 w-auto"
          />
        </div>

        <form
          onSubmit={(event) => void onSubmit(event)}
          className="anim-rise space-y-4 rounded-xl border border-line-subtle bg-raised p-6 shadow-[var(--shadow-card)]"
          noValidate
        >
          {error && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-md border border-danger-border bg-danger-soft px-3 py-2.5"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden />
              <p className="text-xs leading-relaxed text-danger">{error}</p>
            </div>
          )}

          <Field label="Adresse e-mail" htmlFor="email" required>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="prenom.nom@entreprise.com"
              leadingIcon={<Mail className="size-4" />}
              invalid={Boolean(error)}
            />
          </Field>

          <Field label="Mot de passe" htmlFor="password" required>
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              leadingIcon={<Lock className="size-4" />}
              invalid={Boolean(error)}
              trailingSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="rounded p-1 text-ink-faint transition-colors hover:text-ink"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              }
            />
          </Field>

          <Checkbox
            label="Rester connecté sur ce poste"
            checked={rememberMe}
            onChange={(event) => setRememberMe(event.target.checked)}
          />

          <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
            Se connecter
          </Button>
        </form>

        <p className="mt-6 text-center text-2xs text-ink-faint">
          Accès réservé au personnel autorisé. Toutes les connexions sont journalisées.
        </p>
      </div>
    </div>
  );
}
